import intl from 'react-intl-universal';
import { requiredContractTypeIsEmpty } from '@/components/Intl/IntlContext/helper';
import { MemberType, PriceChannel, RoomCleanStatus, SourceChannelType } from '@/config/constant';
import { PRICE_REG } from '@/config/reg';
import { findItemByKey } from '@/redux/actions/reservation/helper';
import {
  CheckInGuestInfo,
  RentRoomInfo,
  RentTimeInfo,
  SourceChannelInfo,
  RoomCategory,
  ChannelItem,
} from '@/redux/reducers/reservation/base.d';
import { isNil } from '@/utils/number';

export type ValidateSchema<T> = {
  [K in keyof T]?: (value: T[K], data: T) => void;
};

const validate: <T>(schema: ValidateSchema<T>, data: T) => string = (schema, data) => {
  try {
    Object.keys(schema).forEach((key) => {
      const itemVal = schema[key];
      if (typeof itemVal === 'function') {
        itemVal(data[key], data);
      } else {
        // validate(itemVal, data);
      }
    });
  } catch (e) {
    return e?.message;
  }
  return '';
};

// 住客信息
export const customerValidator: (
  value: CheckInGuestInfo[],
  data: { isAppendMode: boolean; rentRoomList: Array<RentRoomInfo> },
) => void = (value, data) => {
  const allGuestList = value || [];
  if (!allGuestList.length) throw new Error(intl.get('reservation_error_occupant_null'));
  const { isAppendMode, rentRoomList } = data;
  const guestIdMap = {};
  const filterGuestList: CheckInGuestInfo[] = []; // 校验通过的宾客列表
  allGuestList.forEach((item) => {
    const { indexId, memberType, name, mobile, certType, certNo, remark, wyuId } = item;
    const idMatchValue = `${certType}|${certNo}`; // 证件唯一标识 类型+id
    const isOwner = memberType === MemberType.HouseOwner; // 是否房主
    const guestRole = isOwner
      ? intl.get('config_constant_guest_type_0')
      : intl.get('config_constant_guest_type_1');
    const requiredItem = requiredContractTypeIsEmpty(item);
    if (isOwner && requiredItem.isEmpty && !isAppendMode) {
      // 追加模式下房主肯定有手机号
      // throw new Error('房主手机号未填写，请检查');
      throw new Error(`${intl.get('app_constants_master_name')}${requiredItem.msg}`);
    }
    let err = '';
    if (name || certNo || !requiredItem.isEmpty || remark) {
      // 有一项不为空 填了就必填
      switch (true) {
        case !name:
          err = intl.get('reservation_error_guest_name_null', {
            role: guestRole,
          });
          break;
        case !certNo:
          err = intl.get('reservation_error_guest_cert_null', {
            role: guestRole,
          });
          break;
        case requiredItem.isEmpty:
          err = `${guestRole}${requiredItem.msg}`;
          break;
        // 追加不校验房主脱敏后的手机号
        // case !isAppendMode && mobile && !PHONE_REG.test(mobile):
        //   err = `${guestRole}手机号格式不正确，请检查`;
        //   break;
        case !wyuId:
          err = intl.get('reservation_error_cert_invalid');
          break;
        case !!guestIdMap[idMatchValue]:
          err = intl.get('reservation_error_guest_repeat');
          break;
        default:
          err = '';
      }
      // 填写了信息的宾客
      filterGuestList.push(item);
    }
    if (err) {
      throw new Error(err);
    }
    guestIdMap[idMatchValue] = indexId;
  });
  const guestRoomIdMap: {
    [indexId: string]: number; // 人：房间
  } = {};
  if (rentRoomList.length > 1) {
    // 两间房以上 每个人都要住房
    rentRoomList.forEach((room) => {
      const { roomId, guestList } = room;
      (guestList || []).forEach((guestId) => {
        guestRoomIdMap[guestId] = roomId; // 人 对应的房间id
      });
    });
    filterGuestList.forEach((item) => {
      if (item.memberType === MemberType.HouseOwner && isAppendMode) return; // 追加场景下 不考虑房主
      const roomId = guestRoomIdMap[item.indexId];
      if (!roomId) {
        throw new Error(intl.get('reservation_error_guest_no_room', { name: item.name }));
      }
    });
  }
};

// 入住时间
export const rentTimeValidator: (value: RentTimeInfo) => void = (value) => {
  const { rentTime } = value;
  if (!rentTime) {
    throw new Error(intl.get('reservation_error_duration_null'));
  }
};

// 渠道单号校验器
export const channelOrderNoValidater = (otaBillType, channelOrderNo) => {
  const obj = {
    [PriceChannel.MT]: (val) => /^[\S]{19}$/.test(val?.trim()),
    [PriceChannel.XC]: (val) => /^[\S]{11,16}$/.test(val?.trim()),
    [PriceChannel.DY]: (val) => /^[\S]{19}$/.test(val?.trim()),
  };
  if (typeof obj[otaBillType] === 'function') {
    return obj[otaBillType](channelOrderNo);
  }
  return true;
};

// 渠道信息
export const channelValidator: (
  value: SourceChannelInfo,
  data: { channelList: ChannelItem[] },
) => void = (value, data) => {
  const { channelList } = data;
  const { channelId, channelOrderNo, otaPaymentAmount, invoiceAmount } = value;
  const channelItem = findItemByKey(channelList, 'id', channelId);
  const isOTAChannel = channelItem?.type === SourceChannelType.OTA;
  let err;
  switch (true) {
    case !channelId:
      err = intl.get('reservation_error_channel_null');
      break;
    case isOTAChannel && !channelOrderNo:
      err = intl.get('reservation_error_ota_no_null');
      break;
    case isOTAChannel && !channelOrderNoValidater(channelItem.otaBillType, channelOrderNo):
      err = intl.get('reservation_error_channelNo_format');
      break;
    case isOTAChannel && !PRICE_REG.test(otaPaymentAmount):
      err = intl.get('reservation_error_ota_amount_invalid');
      break;
    case isOTAChannel && isNil(invoiceAmount):
      err = intl.get('reservation_error_invoice_amount_null');
      break;
    case isOTAChannel && !PRICE_REG.test(invoiceAmount):
      err = intl.get('reservation_error_invoice_amount_invalid');
      break;

    default:
      err = '';
  }
  if (err) {
    throw new Error(err);
  }
};

// 房间
export const rentRoomValidator: (
  value: RentRoomInfo[],
  data: { categoryList: RoomCategory[] },
) => void = (value, data) => {
  const roomList = value || [];
  if (!roomList.length) throw new Error(intl.get('reservation_error_room_count_null'));
  const { categoryList } = data;
  const roomStatusMap = {}; // 房态
  categoryList.forEach((item) => {
    (item.roomList || []).forEach((room) => {
      roomStatusMap[room.id] = room.cleanStatus;
    });
  });
  roomList.forEach((item) => {
    const { roomPriceList, deposit, roomId, roomNo } = item;
    switch (true) {
      case !roomNo:
        throw new Error(intl.get('reservation_error_room_no_null'));
      case roomStatusMap[roomId] === RoomCleanStatus.Dirty:
        throw new Error(intl.get('reservation_error_room_dirty'));
      case !deposit:
        throw new Error(intl.get('reservation_error_room_deposit_null'));
      case !PRICE_REG.test(deposit):
        throw new Error(intl.get('reservation_error_room_deposit_invalid'));
      default:
        break;
    }
    (roomPriceList || []).forEach((dayPrice) => {
      if (!dayPrice.inputPrice) {
        throw new Error(intl.get('reservation_error_room_price_null'));
      }
      if (!PRICE_REG.test(dayPrice.inputPrice)) {
        throw new Error(intl.get('reservation_error_room_price_invalid'));
      }
    });
  });
};

export const createValidator: <T>(schema: ValidateSchema<T>) => (data: T) => string | void = (
  schema,
) => (data) => validate(schema, data);
